import React, { useState, useEffect } from 'react';
import axios from 'axios';
import {
    Box,
    Container,
    Paper,
    Tabs,
    Tab,
    Typography,
    TextField,
    Button,
    IconButton,
    Grid,
    Snackbar,
    Alert,
    CircularProgress,
    Dialog,
    DialogTitle,
    DialogContent,
    DialogActions,
    InputAdornment,
    Chip,
    Divider,
    Table,
    TableBody,
    TableCell,
    TableContainer,
    TableHead,
    TableRow,
    TablePagination,
    Tooltip,
    FormControlLabel,
    Switch,
} from '@mui/material';
import {
    Mail,
    Settings,
    History,
    Send,
    Visibility,
    VisibilityOff,
    Delete,
    CheckCircle,
    Error as ErrorIcon,
    Refresh,
    Search,
    Close,
} from '@mui/icons-material';
import { styled } from '@mui/material/styles';

const API_URL = process.env.REACT_APP_API_URL || 'http://localhost:5001';

// Styled Components
const StyledContainer = styled(Container)(({ theme }) => ({
    marginTop: theme.spacing(4),
    marginBottom: theme.spacing(4),
}));

const StyledPaper = styled(Paper)(({ theme }) => ({
    padding: theme.spacing(3),
    borderRadius: theme.spacing(1),
    boxShadow: '0 4px 20px rgba(0, 0, 0, 0.1)',
}));

const TabPanel = ({ children, value, index, ...other }) => (
    <div role="tabpanel" hidden={value !== index} {...other}>
        {value === index && <Box sx={{ p: 3 }}>{children}</Box>}
    </div>
);

const EmailDashboard = () => {
    // State management
    const [tabValue, setTabValue] = useState(0);
    const [settings, setSettings] = useState({
        host: '',
        port: '',
        user: '',
        password: '',
        from: '',
        orderConfirmation: {
            enabled: false,
            subject: 'Order Confirmation',
            template: 'default_order_confirmation',
        },
        shippingUpdate: {
            enabled: false,
            subject: 'Shipping Update',
            template: 'default_shipping_update',
        },
        paymentConfirmation: {
            enabled: false,
            subject: 'Payment Confirmation',
            template: 'default_payment_confirmation',
        },
    });

    const [testEmail, setTestEmail] = useState({
        to: '',
        subject: 'Test Email',
        template: 'test_template',
    });

    const [emailHistory, setEmailHistory] = useState([]);
    const [loading, setLoading] = useState(false);
    const [alert, setAlert] = useState({ open: false, message: '', severity: 'success' });
    const [showPassword, setShowPassword] = useState(false);
    const [page, setPage] = useState(0);
    const [rowsPerPage, setRowsPerPage] = useState(10);
    const [searchTerm, setSearchTerm] = useState('');
    const [filters, setFilters] = useState({ status: 'all' });

    // Effects
    useEffect(() => {
        fetchSettings();
        fetchEmailHistory();
    }, []);

    // API calls
    // Update the settings fetch
    const fetchSettings = async () => {
        try {
            const response = await axios.get(`${API_URL}/api/email/settings`);
            const fetchedSettings = response.data;
            setSettings(fetchedSettings);
        } catch (error) {
            showAlert(error.response?.data?.message || 'Failed to load settings', 'error');
        }
    };

    const fetchEmailHistory = async () => {
        try {
            const response = await axios.get(`${API_URL}/api/email/history`);
            setEmailHistory(response.data.history || []);
        } catch (error) {
            showAlert(error.response?.data?.message || 'Failed to load email history', 'error');
        }
    };

    const saveSettings = async () => {
        if (!validateSettings()) return;

        setLoading(true);
        try {
            await axios.post(`${API_URL}/api/email/settings`, settings);
            showAlert('Settings saved successfully', 'success');
        } catch (error) {
            showAlert(error.response?.data?.message || 'Failed to save settings', 'error');
        } finally {
            setLoading(false);
        }
    };

    const testConnection = async () => {
        setLoading(true);
        try {
            const response = await axios.post(`${API_URL}/api/email/test-connection`);
            showAlert('Connection test successful', 'success');

            // Log the settings if needed
            console.log('Current email settings:', response.data.settings);
        } catch (error) {
            const errorMessage = error.response?.data?.error || error.message;
            showAlert(`Connection test failed: ${errorMessage}`, 'error');
        } finally {
            setLoading(false);
        }
    };

    const sendTestEmail = async () => {
        if (!testEmail.to) {
            showAlert('Please enter a test email address', 'error');
            return;
        }

        setLoading(true);
        try {
            // Create proper email content structure
            const emailData = {
                to: testEmail.to,
                subject: testEmail.subject,
                content: `
                <div style="font-family: Arial, sans-serif; max-width: 600px; margin: 0 auto;">
                    <h2>Test Email</h2>
                    <p>This is a test email sent from your email dashboard.</p>
                    <p>If you received this email, your email system is working correctly.</p>
                    <p>Time sent: ${new Date().toLocaleString()}</p>
                </div>
            `,
            };

            await axios.post(`${API_URL}/api/email/send`, emailData);
            showAlert('Test email sent successfully', 'success');
            setTestEmail({ ...testEmail, to: '' });
        } catch (error) {
            const errorMessage = error.response?.data?.error || error.message || 'Failed to send test email';
            showAlert(errorMessage, 'error');
        } finally {
            setLoading(false);
        }
    };

    // Utility functions
    const validateSettings = () => {
        const requiredFields = ['host', 'port', 'user', 'password', 'from'];
        const missingFields = requiredFields.filter((field) => !settings[field]);

        if (missingFields.length > 0) {
            showAlert(`Please fill in all required fields: ${missingFields.join(', ')}`, 'error');
            return false;
        }

        if (isNaN(settings.port) || settings.port < 1 || settings.port > 65535) {
            showAlert('Please enter a valid port number (1-65535)', 'error');
            return false;
        }

        if (!settings.from.includes('@')) {
            showAlert('Please enter a valid email address in the From field', 'error');
            return false;
        }

        return true;
    };

    const handleTabChange = (event, newValue) => {
        setTabValue(newValue);
    };

    const showAlert = (message, severity) => {
        setAlert({ open: true, message, severity });
    };

    const handleEmailTypeToggle = (type) => {
        setSettings((prev) => ({
            ...prev,
            [type]: {
                ...prev[type],
                enabled: !prev[type].enabled,
            },
        }));
    };

    const handleSettingChange = (type, field, value) => {
        setSettings((prev) => ({
            ...prev,
            [type]: {
                ...prev[type],
                [field]: value,
            },
        }));
    };

    // Filter function for email history
    const filteredHistory = emailHistory.filter(
        (email) =>
            (email.to.toLowerCase().includes(searchTerm.toLowerCase()) || email.subject.toLowerCase().includes(searchTerm.toLowerCase())) &&
            (filters.status === 'all' || email.status === filters.status),
    );

    // Continuing from Part 1...
    return (
        <StyledContainer maxWidth="lg">
            <StyledPaper elevation={0}>
                <Box sx={{ borderBottom: 1, borderColor: 'divider', mb: 3 }}>
                    <Tabs value={tabValue} onChange={handleTabChange} variant="fullWidth" indicatorColor="primary" textColor="primary">
                        <Tab icon={<Settings />} label="Email Settings" iconPosition="start" sx={{ minHeight: 64 }} />
                        <Tab icon={<History />} label="Email History" iconPosition="start" sx={{ minHeight: 64 }} />
                    </Tabs>
                </Box>

                {/* Settings Tab */}
                <TabPanel value={tabValue} index={0}>
                    <Grid container spacing={3}>
                        <Grid item xs={12}>
                            <Typography variant="h6" gutterBottom>
                                SMTP Settings
                            </Typography>
                        </Grid>

                        <Grid item xs={12} md={6}>
                            <TextField
                                fullWidth
                                label="SMTP Host"
                                value={settings.host}
                                onChange={(e) => setSettings({ ...settings, host: e.target.value })}
                                variant="outlined"
                            />
                        </Grid>
                        <Grid item xs={12} md={6}>
                            <TextField
                                fullWidth
                                label="SMTP Port"
                                value={settings.port}
                                onChange={(e) => setSettings({ ...settings, port: e.target.value })}
                                variant="outlined"
                                type="number"
                            />
                        </Grid>
                        <Grid item xs={12} md={6}>
                            <TextField
                                fullWidth
                                label="Username"
                                value={settings.user}
                                onChange={(e) => setSettings({ ...settings, user: e.target.value })}
                                variant="outlined"
                            />
                        </Grid>
                        <Grid item xs={12} md={6}>
                            <TextField
                                fullWidth
                                label="Password"
                                type={showPassword ? 'text' : 'password'}
                                value={settings.password}
                                onChange={(e) => setSettings({ ...settings, password: e.target.value })}
                                variant="outlined"
                                InputProps={{
                                    endAdornment: (
                                        <InputAdornment position="end">
                                            <IconButton onClick={() => setShowPassword(!showPassword)} edge="end">
                                                {showPassword ? <VisibilityOff /> : <Visibility />}
                                            </IconButton>
                                        </InputAdornment>
                                    ),
                                }}
                            />
                        </Grid>
                        <Grid item xs={12}>
                            <TextField
                                fullWidth
                                label="From Address"
                                value={settings.from}
                                onChange={(e) => setSettings({ ...settings, from: e.target.value })}
                                variant="outlined"
                            />
                        </Grid>

                        <Grid item xs={12}>
                            <Typography variant="h6" gutterBottom sx={{ mt: 3 }}>
                                Automatic Emails
                            </Typography>
                        </Grid>

                        {/* Order Confirmation Email */}
                        <Grid item xs={12}>
                            <FormControlLabel
                                control={
                                    <Switch
                                        checked={settings.orderConfirmation.enabled}
                                        onChange={() => handleEmailTypeToggle('orderConfirmation')}
                                    />
                                }
                                label="Order Confirmation Email"
                            />
                            {settings.orderConfirmation.enabled && (
                                <TextField
                                    fullWidth
                                    label="Subject"
                                    y
                                    value={settings.orderConfirmation.subject}
                                    onChange={(e) => handleSettingChange('orderConfirmation', 'subject', e.target.value)}
                                    variant="outlined"
                                    sx={{ mt: 2 }}
                                />
                            )}
                        </Grid>

                        {/* Shipping Update Email */}
                        <Grid item xs={12}>
                            <FormControlLabel
                                control={
                                    <Switch
                                        checked={settings.shippingUpdate.enabled}
                                        onChange={() => handleEmailTypeToggle('shippingUpdate')}
                                    />
                                }
                                label="Shipping Update Email"
                            />
                            {settings.shippingUpdate.enabled && (
                                <TextField
                                    fullWidth
                                    label="Subject"
                                    value={settings.shippingUpdate.subject}
                                    onChange={(e) => handleSettingChange('shippingUpdate', 'subject', e.target.value)}
                                    variant="outlined"
                                    sx={{ mt: 2 }}
                                />
                            )}
                        </Grid>

                        {/* Payment Confirmation Email */}
                        <Grid item xs={12}>
                            <FormControlLabel
                                control={
                                    <Switch
                                        checked={settings.paymentConfirmation.enabled}
                                        onChange={() => handleEmailTypeToggle('paymentConfirmation')}
                                    />
                                }
                                label="Payment Confirmation Email"
                            />
                            {settings.paymentConfirmation.enabled && (
                                <TextField
                                    fullWidth
                                    label="Subject"
                                    value={settings.paymentConfirmation.subject}
                                    onChange={(e) => handleSettingChange('paymentConfirmation', 'subject', e.target.value)}
                                    variant="outlined"
                                    sx={{ mt: 2 }}
                                />
                            )}
                        </Grid>

                        {/* Test Email Section */}
                        <Grid item xs={12}>
                            <Typography variant="h6" gutterBottom sx={{ mt: 3 }}>
                                Test Email
                            </Typography>
                            <TextField
                                fullWidth
                                label="Test Email Address"
                                value={testEmail.to}
                                onChange={(e) => setTestEmail({ ...testEmail, to: e.target.value })}
                                variant="outlined"
                                sx={{ mb: 2 }}
                            />
                        </Grid>

                        {/* Action Buttons */}
                        <Grid item xs={12}>
                            <Button
                                variant="contained"
                                color="primary"
                                onClick={saveSettings}
                                disabled={loading}
                                startIcon={loading ? <CircularProgress size={20} /> : <Settings />}
                                sx={{ mr: 2 }}
                            >
                                Save Settings
                            </Button>
                            <Button
                                variant="outlined"
                                color="primary"
                                onClick={testConnection}
                                disabled={loading}
                                startIcon={<Refresh />}
                                sx={{ mr: 2 }}
                            >
                                Test Connection
                            </Button>
                            <Button
                                variant="outlined"
                                color="secondary"
                                onClick={sendTestEmail}
                                disabled={loading || !testEmail.to}
                                startIcon={<Send />}
                            >
                                Send Test Email
                            </Button>
                        </Grid>
                    </Grid>
                </TabPanel>

                {/* History Tab */}
                <TabPanel value={tabValue} index={1}>
                    <Box sx={{ mb: 3 }}>
                        <Grid container spacing={2} alignItems="center">
                            <Grid item xs={12} md={6}>
                                <TextField
                                    fullWidth
                                    placeholder="Search emails..."
                                    value={searchTerm}
                                    onChange={(e) => setSearchTerm(e.target.value)}
                                    InputProps={{
                                        startAdornment: (
                                            <InputAdornment position="start">
                                                <Search />
                                            </InputAdornment>
                                        ),
                                    }}
                                    variant="outlined"
                                />
                            </Grid>
                            <Grid item xs={12} md={6}>
                                <Box sx={{ display: 'flex', gap: 1 }}>
                                    {['all', 'sent', 'failed'].map((status) => (
                                        <Chip
                                            key={status}
                                            label={status.charAt(0).toUpperCase() + status.slice(1)}
                                            onClick={() => setFilters({ ...filters, status })}
                                            color={filters.status === status ? 'primary' : 'default'}
                                            variant={filters.status === status ? 'filled' : 'outlined'}
                                        />
                                    ))}
                                </Box>
                            </Grid>
                        </Grid>
                    </Box>

                    <TableContainer>
                        <Table>
                            <TableHead>
                                <TableRow>
                                    <TableCell>Status</TableCell>
                                    <TableCell>Type</TableCell>
                                    <TableCell>To</TableCell>
                                    <TableCell>Subject</TableCell>
                                    <TableCell>Date</TableCell>
                                </TableRow>
                            </TableHead>
                            <TableBody>
                                {filteredHistory.slice(page * rowsPerPage, page * rowsPerPage + rowsPerPage).map((email, index) => (
                                    <TableRow key={index}>
                                        <TableCell>
                                            <Tooltip title={email.error || 'Sent successfully'}>
                                                <Box component="span">
                                                    {email.status === 'sent' ? (
                                                        <CheckCircle color="success" />
                                                    ) : (
                                                        <ErrorIcon color="error" />
                                                    )}
                                                </Box>
                                            </Tooltip>
                                        </TableCell>
                                        <TableCell>{email.type || 'Test'}</TableCell>
                                        <TableCell>{email.to}</TableCell>
                                        <TableCell>{email.subject}</TableCell>
                                        <TableCell>{new Date(email.timestamp).toLocaleString()}</TableCell>
                                    </TableRow>
                                ))}
                            </TableBody>
                        </Table>
                    </TableContainer>

                    <TablePagination
                        component="div"
                        count={filteredHistory.length}
                        page={page}
                        onPageChange={(e, newPage) => setPage(newPage)}
                        rowsPerPage={rowsPerPage}
                        onRowsPerPageChange={(e) => {
                            setRowsPerPage(parseInt(e.target.value, 10));
                            setPage(0);
                        }}
                    />
                </TabPanel>

                {/* Alert Snackbar */}
                <Snackbar
                    open={alert.open}
                    autoHideDuration={6000}
                    onClose={() => setAlert({ ...alert, open: false })}
                    anchorOrigin={{ vertical: 'bottom', horizontal: 'right' }}
                >
                    <Alert
                        onClose={() => setAlert({ ...alert, open: false })}
                        severity={alert.severity}
                        variant="filled"
                        sx={{ width: '100%' }}
                    >
                        {alert.message}
                    </Alert>
                </Snackbar>
            </StyledPaper>
        </StyledContainer>
    );
};

export default EmailDashboard;
